import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../../design-system';
import StateSelect from '../../../components/StateSelect';
import '../../styles/Finance.css';

// The firm's billing identity, its number series, its tax defaults and how it
// talks to clients about money.
//
// ⚠️ Every field rendered here must also be in updateFinanceSettings' `allowed`
// whitelist on the server. A field that is only in the response reads back
// correctly and silently discards every save — which is exactly how the chat
// settings' showStaffNamesToClient toggle shipped doing nothing.

const FIELDS_HELP = {
  stateCode: 'Decides CGST+SGST vs IGST on every invoice — without it, each one guesses.',
};

// Mirrors the server's rule in updateFinanceSettings. The server is still the
// enforcer; this only saves the user a save round-trip to be told no.
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_IMAGE_BYTES = 400 * 1024;

// Pick an image, hold it as a base64 data URI on the form, preview it, clear it.
// No upload endpoint: the value IS the field, so it saves with everything else
// and a half-finished edit leaves nothing orphaned behind.
function ImageField({ label, value, onChange, disabled, hint, toast, small }) {
  const read = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so re-picking the same file fires again
    if (!file) return;
    // SVG is refused: these render in the client portal and inside an email
    // body, and an SVG is a script vector.
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.danger('Use a PNG, JPEG, WEBP or GIF. SVG is not accepted.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.danger(`That image is ${Math.round(file.size / 1024)}KB — keep it under ${MAX_IMAGE_BYTES / 1024}KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.onerror = () => toast.danger('Could not read that file');
    reader.readAsDataURL(file);
  };

  return (
    <div className="ds-field fin-imgfield">
      <span className="ds-field-label">{label}</span>
      {value ? (
        <div className={`fin-imgprev${small ? ' is-small' : ''}`}>
          <img src={value} alt={`${label} preview`} />
        </div>
      ) : (
        <div className={`fin-imgprev is-empty${small ? ' is-small' : ''}`}>
          <span>No {label.toLowerCase()} yet</span>
        </div>
      )}
      {!disabled && (
        <div className="fin-imgactions">
          <label className="fin-imgbtn">
            {value ? 'Replace' : 'Choose image'}
            <input type="file" accept={IMAGE_TYPES.join(',')} onChange={read} />
          </label>
          {value && (
            <button type="button" className="fin-link fin-link-btn" onClick={() => onChange('')}>
              Remove
            </button>
          )}
        </div>
      )}
      {hint && <span className="ds-field-helper">{hint}</span>}
    </div>
  );
}

function FinanceSettingsCard() {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [canWrite, setCanWrite] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    apiClient.get('/api/finance/settings')
      .then((r) => {
        setForm(r.data.settings);
        setCanWrite(r.data.canWrite);
        setEmailConfigured(r.data.emailConfigured !== false);
      })
      .catch((e) => toast.danger(e.response?.data?.error || 'Could not load finance settings'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/finance/settings', form);
      toast.success('Finance settings saved');
      load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <Card><Skeleton.Text lines={8} /></Card>;

  const ro = !canWrite;
  const fy = new Date().getMonth() >= 3
    ? `${new Date().getFullYear()}-${String((new Date().getFullYear() + 1) % 100).padStart(2, '0')}`
    : `${new Date().getFullYear() - 1}-${String(new Date().getFullYear() % 100).padStart(2, '0')}`;
  const sample = (prefix) => (form.resetSeriesEachFY
    ? `${prefix}${fy}/${'1'.padStart(form.numberPadding || 4, '0')}`
    : `${prefix}${'1'.padStart(form.numberPadding || 4, '0')}`);

  return (
    <div className="fin-stack">
      <Card>
        <h3 className="fin-h">Your firm, as a supplier</h3>
        <p className="fin-sub">
          What appears at the top of every invoice. The state code is load-bearing: it is what
          decides whether an invoice carries CGST+SGST or IGST.
        </p>
        <div className="fin-form">
          <div className="fin-form-row">
            <Input label="Legal name" value={form.legalName || ''} onChange={set('legalName')} disabled={ro} />
            <Input label="GSTIN" value={form.gstin || ''} onChange={set('gstin')} disabled={ro} />
          </div>
          <div className="fin-form-row">
            <Input label="PAN" value={form.pan || ''} onChange={set('pan')} disabled={ro} />
            {/* One picker, both fields. They were two free-text inputs, so the
                firm's own state could be saved as "Haryana / 27" — and this is
                the code every client's state is measured AGAINST, so one wrong
                digit here mis-heads the tax on every invoice at once. */}
            <StateSelect
              label="State"
              code={form.stateCode}
              name={form.stateName}
              disabled={ro}
              helperText={FIELDS_HELP.stateCode}
              onChange={({ stateCode, state }) =>
                setForm((f) => ({ ...f, stateCode, stateName: state }))}
            />
          </div>
          <Input label="Address" value={form.address || ''} onChange={set('address')} disabled={ro} />
        </div>
      </Card>

      <Card>
        <h3 className="fin-h">Numbering</h3>
        <p className="fin-sub">
          Three separate series, and they must stay separate. A proforma is not a document under
          GST, so numbering one out of the tax-invoice series either duplicates a real invoice
          number or spends one on a supply that never happens — both leave a hole an auditor asks
          about.
        </p>
        <div className="fin-form">
          <div className="fin-form-row">
            <Input
              label="Tax invoice prefix" value={form.invoicePrefix || ''} onChange={set('invoicePrefix')}
              disabled={ro} helperText={sample(form.invoicePrefix || 'INV/')}
            />
            <Input
              label="Proforma prefix" value={form.proformaPrefix || ''} onChange={set('proformaPrefix')}
              disabled={ro} helperText={sample(form.proformaPrefix || 'PI/')}
            />
            <Input
              label="Receipt prefix" value={form.receiptPrefix || ''} onChange={set('receiptPrefix')}
              disabled={ro} helperText={sample(form.receiptPrefix || 'RCPT/')}
            />
          </div>
          <div className="fin-form-row">
            <Select
              label="Restart each financial year"
              value={form.resetSeriesEachFY ? 'yes' : 'no'}
              onChange={(e) => setForm((f) => ({ ...f, resetSeriesEachFY: e.target.value === 'yes' }))}
              disabled={ro}
            >
              <option value="yes">Yes — restart on 1 April</option>
              <option value="no">No — one running series</option>
            </Select>
            <Input
              label="Digits" type="number" min="1" max="8"
              value={form.numberPadding ?? 4} onChange={set('numberPadding')} disabled={ro}
            />
            <Input
              label="Proforma valid for (days)" type="number" min="0"
              value={form.proformaValidityDays ?? 15} onChange={set('proformaValidityDays')}
              disabled={ro} helperText="Printed on the proforma. Nothing expires automatically."
            />
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="fin-h">Tax defaults</h3>
        <div className="fin-form">
          <div className="fin-form-row">
            <Input
              label="GST rate %" type="number" min="0" max="100" step="0.01"
              value={form.gstRate ?? 18} onChange={set('gstRate')} disabled={ro}
            />
            <Input
              label="TDS % (u/s 194J)" type="number" min="0" max="100" step="0.01"
              value={form.tdsPercent ?? 10} onChange={set('tdsPercent')} disabled={ro}
              helperText="What the client deducts. Applied to the fee, never to the GST over it."
            />
            <Input
              label="Payment terms (days)" type="number" min="0"
              value={form.paymentTermDays ?? 15} onChange={set('paymentTermDays')} disabled={ro}
            />
          </div>
          <label className="fin-check">
            <input
              type="checkbox" checked={form.gstApplicableByDefault !== false}
              onChange={set('gstApplicableByDefault')} disabled={ro}
            />
            <span>Charge GST by default</span>
          </label>
          <label className="fin-check">
            <input
              type="checkbox" checked={form.tdsAppliesByDefault !== false}
              onChange={set('tdsAppliesByDefault')} disabled={ro}
            />
            <span>Expect the client to deduct TDS by default</span>
          </label>
          <label className="fin-check">
            <input
              type="checkbox" checked={form.opeIsPureAgent !== false}
              onChange={set('opeIsPureAgent')} disabled={ro}
            />
            <span>
              Reimbursements are recovered as a pure agent — no GST
              <span className="fin-muted"> (filing fees, stamp duty paid on the client's behalf)</span>
            </span>
          </label>
        </div>
      </Card>

      <Card>
        <h3 className="fin-h">Client payments</h3>
        {!emailConfigured && (
          <p className="fin-note-warn">
            <b>Email is not configured on this server.</b> Documents will be prepared and visible
            in the client portal, but nothing will actually be emailed. Set{' '}
            <code>SMTP_HOST</code>, <code>SMTP_PORT</code>, <code>SMTP_USER</code>,{' '}
            <code>SMTP_PASS</code> and <code>MAIL_FROM</code> in the server environment.
          </p>
        )}
        <div className="fin-form">
          <Select
            label="Send dialog opens on"
            value={form.defaultSendKind || 'proforma'}
            onChange={set('defaultSendKind')}
            disabled={ro}
            helperText="Your usual practice, pre-selected. The sender still picks per invoice — a proforma where the fee may still be queried, a tax invoice for work delivered on agreed terms."
          >
            <option value="proforma">Proforma invoice</option>
            <option value="tax_invoice">Tax invoice</option>
          </Select>

          <label className="fin-check">
            <input
              type="checkbox" checked={form.autoIssueOnPaymentApproval !== false}
              onChange={set('autoIssueOnPaymentApproval')} disabled={ro}
            />
            <span>
              Issue the tax invoice automatically when a client's payment is confirmed
              <span className="fin-muted">
                {' '}— receiving payment for a service is the time of supply, so a statutory
                document is due at that moment. Turning this off leaves the firm holding money
                against no invoice until someone raises one by hand.
              </span>
            </span>
          </label>

          <div className="fin-form-row">
            <Input
              label="Send emails as" value={form.emailFromName || ''} onChange={set('emailFromName')}
              disabled={ro} helperText="The display name clients see. Defaults to your legal name."
            />
            <Input
              label="Reply-to address" value={form.replyToEmail || ''} onChange={set('replyToEmail')}
              disabled={ro} helperText="Where a client's reply should land."
            />
          </div>

          <Input
            label="Payment instructions"
            value={form.paymentInstructions || ''} onChange={set('paymentInstructions')} disabled={ro}
            helperText="Shown to the client next to the amount payable, in the portal and the email."
          />
          <Input
            label="Bank details"
            value={form.bankDetails || ''} onChange={set('bankDetails')} disabled={ro}
            helperText="Printed on the document itself."
          />
          <Input
            label="Standing invoice note"
            value={form.invoiceNotes || ''} onChange={set('invoiceNotes')} disabled={ro}
          />
        </div>
      </Card>

      <Card>
        <h3 className="fin-h">Letterhead and signature</h3>
        <p className="fin-sub">
          Printed on every proforma, tax invoice and receipt — in the client portal, in the emailed
          copy and when the document is printed. Images are stored on the document itself rather than
          as a link, so an invoice sent today still renders years later.
        </p>
        <div className="fin-form">
          <div className="fin-form-row">
            <ImageField
              label="Letterhead" value={form.letterheadDataUrl} disabled={ro}
              onChange={(v) => setForm((f) => ({ ...f, letterheadDataUrl: v }))}
              hint="Runs full width across the top. A wide strip (roughly 6:1) prints best."
              toast={toast}
            />
            <ImageField
              label="Signature" value={form.signatureDataUrl} disabled={ro}
              onChange={(v) => setForm((f) => ({ ...f, signatureDataUrl: v }))}
              hint="Sits above the signatory line. A transparent PNG looks best."
              toast={toast} small
            />
          </div>

          <div className="fin-form-row">
            <Input
              label="Signatory name"
              value={form.signatoryName || ''} onChange={set('signatoryName')} disabled={ro}
              helperText="Printed under the signature. Leave blank for just the line."
            />
            <Input
              label="Designation"
              value={form.signatoryDesignation || ''} onChange={set('signatoryDesignation')} disabled={ro}
              helperText='Defaults to "Authorised signatory".'
            />
            <Select
              label="Firm name and address block"
              value={form.letterheadReplacesHeader ? 'hide' : 'show'}
              onChange={(e) => setForm((f) => ({ ...f, letterheadReplacesHeader: e.target.value === 'hide' }))}
              disabled={ro || !form.letterheadDataUrl}
            >
              <option value="show">Print it as well</option>
              <option value="hide">Letterhead already shows it</option>
            </Select>
          </div>
          {/* GSTIN and PAN are required on a tax invoice, so they print either
              way — a letterhead often doesn't carry them. */}
          <p className="fin-sub">
            GSTIN and PAN are always printed, whichever option you choose: a tax invoice must carry
            them and a letterhead usually doesn't.
          </p>
        </div>
      </Card>

      {canWrite && (
        <div>
          <Button variant="primary" onClick={save} loading={saving}>Save finance settings</Button>
        </div>
      )}
    </div>
  );
}

export default FinanceSettingsCard;
